import { DateUtil, DateWrapper, SchoolUtil, StringUtil, Weekday } from 'kuaidong-lib'
import {
  Clazz,
  CourseLiveStatus,
  ICourseLive,
  ICourseResourceGradeTag,
  ICourseResourceGradeTagClassMap,
  ICourseScheduleItem,
  ILiveCourseDetail,
  IOnlineCourseRepo,
  IOnlineCourseTag,
  ICourseResourcePreview,
  ICourseResourcePreviewRepoItem,
  ICourseRepoDetailItemDrill,
  ICourseRepoDetailItem,
} from 'kuaidong-model'
import { TeacherService } from './TeacherService'
import groupBy from 'lodash/groupBy'
import cloneDeep from 'lodash/cloneDeep'

export class CourseLiveService {
  constructor(public model: ICourseLive) {}

  get courseClazzCount() {
    return this.model.detail?.class_list.length || '-'
  }
  get timeRange() {
    const courseStartTime = new DateWrapper(this.model.start_time)
    const courseEndTime = new DateWrapper(courseStartTime.date.getTime() + this.model.duration * 60 * 1000)
    return `${courseStartTime.showTimeStr}~${courseEndTime.showTimeStr}`
  }

  get dateRange() {
    const courseStartTime = new DateWrapper(this.model.start_time)
    const courseEndTime = new DateWrapper(this.model.end_time)
    return `${courseStartTime.showDateStr}~${courseEndTime.showDateStr}`
  }

  get startDateStr() {
    return new DateWrapper(this.model.start_time).showStrFullDate
  }

  get endDateStr() {
    return new DateWrapper(this.model.end_time).showStrFullDate
  }

  get isParticipated() {
    return this.model.marked
  }

  get liveStatusLabel() {
    switch (this.model.live_status) {
      case CourseLiveStatus.Open:
        return '未开始'
      case CourseLiveStatus.InProgress:
        return '进行中'
      case CourseLiveStatus.Ended:
        return '已结束'
      default:
        throw new Error('Wrong live status.')
    }
  }

  get isLiveStatusInProgress() {
    return this.model.live_status === CourseLiveStatus.InProgress
  }

  get classNameList() {
    return (this.model.detail?.class_list || [])
      .map((clazzInfo: Clazz) => {
        return clazzInfo.name
      })
      .join('、')
  }
  get coachName() {
    return TeacherService.buildName(this.model.coach.name)
  }
}

export class NewCourseLiveService {
  public static showCourseCycleType(cycle: number) {
    if (cycle === 1) {
      return '每日一节'
    } else if (cycle === 7) {
      return '每周一节'
    }
    return null
  }

  public static showLessonLength(courseDetail: ILiveCourseDetail | null) {
    if (courseDetail) {
      return (
        (courseDetail.repo_item_id_list && courseDetail.repo_item_id_list.length) ||
        (courseDetail && courseDetail.online_course_item_list.length)
      )
    } else {
      return null
    }
  }
  public static getGradeClass(gradeTag: string) {
    return ICourseResourceGradeTagClassMap[gradeTag as keyof ICourseResourceGradeTag]
  }

  public static formatPlanIndex(index: number) {
    return StringUtil.padZero(index + 1, 2)
  }

  public static buildTagOptions(tags: IOnlineCourseTag[]) {
    return tags.map((item) => {
      return {
        label: item.tag_name,
        value: item.tag_name,
      }
    })
  }

  public static buildCategoryFromRepoList(liveCourseRepoList: IOnlineCourseRepo[]) {
    return Array.from(new Set(liveCourseRepoList.map((repo) => repo.category)))
  }

  public static filterRepoListByCourseNameAndTag(
    liveCourseRepoList: IOnlineCourseRepo[],
    filterCondition: { course_name: string; tag: string }
  ) {
    const generalCourseRepoList = liveCourseRepoList.filter((item) => {
      item.tag_list.includes('通用')
    })

    let res =
      filterCondition.course_name === ''
        ? liveCourseRepoList
        : liveCourseRepoList.filter((item) => {
            return item.name.includes(filterCondition.course_name)
          })

    res = filterCondition.tag === '' ? res : res.filter((item) => item.tag_list.includes(filterCondition.tag))

    return [...generalCourseRepoList, ...res]
  }

  // 显示课节开始时间
  public static buildLessonStartDate(dt: string | Date) {
    const dateWrapper = new DateWrapper(dt)
    if (dateWrapper.isCurrentYear()) {
      return `${StringUtil.padZero(dateWrapper.date.getMonth() + 1)}/${StringUtil.padZero(dateWrapper.date.getDate())}`
    } else {
      return `${dateWrapper.date.getFullYear()}/${StringUtil.padZero(
        dateWrapper.date.getMonth() + 1
      )}/${StringUtil.padZero(dateWrapper.date.getDate())}`
    }
  }
  public static buildLessonStartDateCN(dt: string) {
    const dateWrapper = new DateWrapper(dt)
    if (dateWrapper.isCurrentYear()) {
      return `${StringUtil.padZero(dateWrapper.date.getMonth() + 1)}月${StringUtil.padZero(
        dateWrapper.date.getDate()
      )}日`
    } else {
      return `${dateWrapper.date.getFullYear()}年${StringUtil.padZero(
        dateWrapper.date.getMonth() + 1
      )}月${StringUtil.padZero(dateWrapper.date.getDate())}日`
    }
  }
  // 构建课程表
  public static buildLessonScheduleDateList(scheduleItems: ICourseScheduleItem[]) {
    const sortLessons = scheduleItems.sort((a, b) => {
      return new Date(b.current_datetime).getTime() - new Date(a.current_datetime).getTime()
    })
    const res = sortLessons.map((item) => {
      return {
        ...item,
        date: this.buildLessonStartDateCN(item.current_datetime),
      }
    })
    return groupBy(res, 'date')
  }

  // 课程布置方式
  public static buildCourseStartLabel(weekNo: Weekday, courseCycle: number | null) {
    if (courseCycle === 7) {
      return DateUtil.formatWeekday(weekNo, '每周')
    } else if (courseCycle === 1) {
      return '每日'
    } else {
      return null
    }
  }
  // 显示课程上课时间
  public static buildCourseStartTime(dt: string | Date, courseCycle: number | null) {
    if (courseCycle === null) {
      return `${this.buildLessonStartDate(dt)} ${new DateWrapper(dt).showTimeStr}`
    } else {
      return new DateWrapper(dt).showTimeStr
    }
  }

  // 显示课程上课时间标签颜色
  public static buildCourseStartTimeLabel(dt: string) {
    const dw = new DateWrapper(dt)
    const now = new DateWrapper(new Date())
    if (dw.isToday()) {
      return 'today'
    } else if (DateUtil.beginningOfDay(now.date) > DateUtil.endOfDay(dw.date)) {
      return 'pass'
    } else {
      return 'future'
    }
  }

  // 构建课程详情中的课程信息
  public static buildLessonInfo(courseDetail: ILiveCourseDetail | null) {
    if (courseDetail) {
      return [
        {
          label: '开课日期',
          value: new DateWrapper(courseDetail.start_datetime).showDateStr,
        },
        {
          label: '结束日期',
          value: new DateWrapper(courseDetail.end_datetime).showDateStr,
        },
        {
          label: '上课时间',
          value: new DateWrapper(courseDetail.start_datetime).showTimeStr,
        },
        {
          label: '课程节数',
          value: `${courseDetail.repo_item_id_list && courseDetail.repo_item_id_list.length}节`,
        },
      ]
    } else {
      return []
    }
  }

  // 获取当前课程的班级范围
  public static buildClazzRangeList(classList?: Clazz[]) {
    if (classList && classList.length) {
      return classList.map((item) => {
        return {
          label: item.note_name || item.name || SchoolUtil.buildClazzName(item.grade_no, item.class_no),
          value: item.id,
        }
      })
    } else {
      return []
    }
  }
  //  构建课程详情班级信息
  public static buildClazzInfoGroupByGrade(clazzList: Clazz[]) {
    const clazzMap = clazzList.map((item) => {
      return {
        ...item,
        gradeName: SchoolUtil.buildGradeName(item.grade_no),
      }
    })
    const groups = groupBy(clazzMap, 'gradeName')
    const gradeNames = Object.keys(groups)
    const gradeInfo: {
      gradeName: string
      clazzName: string[]
    }[] = []
    gradeNames.forEach((gradeName) => {
      gradeInfo.push({
        gradeName: gradeName,
        clazzName: groups[gradeName].map((clazz) => {
          return clazz.note_name ? clazz.note_name : `${clazz.class_no}班`
        }),
      })
    })
    return gradeInfo
  }

  public static participatedRateColor(percent: number) {
    if (percent > 100) {
      percent = percent / 100
    }

    if (percent >= 0 && percent <= 20) {
      return {
        color: '#F24B53',
      }
    } else if (percent > 20 && percent <= 50) {
      return {
        color: '#FE9647',
      }
    } else {
      return {
        color: '#3BB528',
      }
    }
  }

  public static showClazzName(clazzInfos: Clazz[]) {
    const clazzName = clazzInfos.length ? clazzInfos[0].note_name || clazzInfos[0].name : ''
    return clazzInfos.length > 1 ? `${clazzName}等${clazzInfos.length}个班级` : clazzName
  }

  public static showAllClazzName(clazzInfos: Clazz[]) {
    return clazzInfos
      .map((item) => {
        return item.note_name || item.name
      })
      .join('、')
  }
}

abstract class OnlineCourseLesson {
  date: Date

  constructor(date: Date) {
    this.date = date
  }

  createMemento(): Date {
    return this.date
  }

  restore(date: Date): void {
    this.date = date
  }
}

export class OnlineCourseLessonUnderAssign extends OnlineCourseLesson {
  repo_item_id: string
  title: string
  duration: number
  energy: number
  drill_number: number
  video_url: string
  difficult_level: string
  description: string
  repo_item_drills: ICourseRepoDetailItemDrill[]
  homework_template_id: string | null
  constructor(item: ICourseResourcePreviewRepoItem) {
    super(new Date(item.current_datetime))
    this.repo_item_id = item.repo_item_id
    this.title = item.title
    this.duration = item.duration
    this.energy = item.energy
    this.drill_number = item.drill_number
    this.video_url = item.video_url
    this.difficult_level = item.difficult_level
    this.description = item.description
    this.repo_item_drills = item.repo_item_drills
    this.homework_template_id = item.homework_template_id
  }

  buildItem(): ICourseResourcePreviewRepoItem {
    return {
      repo_item_id: this.repo_item_id,
      title: this.title,
      duration: this.duration,
      energy: this.energy,
      drill_number: this.drill_number,
      video_url: this.video_url,
      difficult_level: this.difficult_level,
      description: this.description,
      repo_item_drills: this.repo_item_drills,
      current_datetime: this.date.toISOString(),
      homework_template_id: this.homework_template_id,
    }
  }
}

export class OnlineCourseLessonAfterAssign extends OnlineCourseLesson {
  course_item_id: string
  title: string
  duration: number
  energy: number
  drill_number: number
  video_url: string
  difficult_level: string
  description: string

  constructor(item: ICourseRepoDetailItem) {
    super(new Date(item.current_datetime!))
    this.course_item_id = item.course_item_id!
    this.title = item.title
    this.duration = item.duration
    this.energy = item.energy
    this.drill_number = item.drill_number
    this.video_url = item.video_url
    this.difficult_level = item.difficult_level
    this.description = item.description
  }
}

export class OnlineCourseLessonsCollections {
  items: OnlineCourseLesson[]
  updatePosition: null | number = null
  step = 7

  constructor(items: OnlineCourseLesson[], step?: number) {
    this.items = items
    if (step) this.step = step
    const pre = cloneDeep(this.items[0])
    pre.date = DateUtil.addDay(this.items[0].date, -this.step)
    this.items.unshift(pre)
  }

  get isTailPosition() {
    return this.updatePosition === this.items.length - 1
  }

  updatePre(date: Date) {
    this.items[0].date = DateUtil.addDay(date, -this.step)
  }

  insertLessonByDate(lesson: OnlineCourseLesson) {
    const index = this.items.slice(1).findIndex((item) => item.date >= lesson.date)
    if (index === -1) {
      this.items.push(lesson)
      this.updatePosition = this.items.length - 1
    } else {
      this.items.splice(index + 1, 0, lesson)
      this.updatePosition = index + 1
    }
    this.updatePre(this.items[1].date)
  }

  removeLesson(index: number) {
    const lesson = this.items[index]
    this.items.splice(index, 1)
    this.updatePosition = index - 1
    if (this.updatePosition === 0) {
      this.updatePre(lesson.date)
    }
    return lesson
  }

  updateLesson(index: number) {
    const lesson = this.removeLesson(index)
    this.insertLessonByDate(lesson)
  }

  updateDateOflessons() {
    const target = this.items[this.updatePosition!]
    if (this.updatePosition === 0) {
      for (let i = 1; i < this.items.length; i++) {
        const date = this.items[i].date
        const hours = date.getHours()
        const minutes = date.getMinutes()
        this.items[i].date = DateUtil.mergeDateAndTime(
          DateUtil.addDay(target.date, i * this.step),
          `${hours}:${minutes}`
        )
      }
    } else {
      for (let i = this.updatePosition! + 1; i < this.items.length; i++) {
        this.items[i].date = DateUtil.addDay(target.date, (i - this.updatePosition!) * this.step)
      }
    }
  }
}

export class OnlineCoursePreviewInfo {
  repository_id: string
  title: string
  start_datetime: string
  course_cycle: number
  week_no: number
  class_id_list: number[]
  lessons: OnlineCourseLessonsCollections
  constructor(info: ICourseResourcePreview) {
    this.repository_id = info.repository_id
    this.title = info.title
    this.start_datetime = info.start_datetime
    this.course_cycle = info.course_cycle
    this.week_no = info.week_no
    this.class_id_list = info.class_id_list
    this.lessons = new OnlineCourseLessonsCollections(
      info.repo_item_list.map((item) => new OnlineCourseLessonUnderAssign(item)),
      this.course_cycle
    )
  }

  createModifiedItems(): {
    repo_item_id: string
    current_datetime: string
  }[] {
    return this.lessons.items.slice(1).map((item) => ({
      repo_item_id: (item as OnlineCourseLessonUnderAssign).repo_item_id,
      current_datetime: item.date.toISOString(),
    }))
  }
}

export class OnlineCourseUpdateInfo {
  online_course_id: string
  start_datetime: string
  course_cycle: number
  lessons: OnlineCourseLessonsCollections

  constructor(info: ILiveCourseDetail) {
    this.online_course_id = info.online_course_id
    this.start_datetime = info.start_datetime
    this.course_cycle = info.course_cycle
    this.lessons = new OnlineCourseLessonsCollections(
      info.online_course_item_list.map((item) => new OnlineCourseLessonAfterAssign(item)),
      this.course_cycle
    )
  }

  createInfoAfterUpdate(): {
    online_course_id: string
    course_items: {
      course_item_id: string
      current_datetime: string
    }[]
  } {
    return {
      online_course_id: this.online_course_id,
      course_items: this.lessons.items.slice(1).map((item) => ({
        course_item_id: (item as OnlineCourseLessonAfterAssign).course_item_id,
        current_datetime: item.date.toISOString(),
      })),
    }
  }
}
